import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

/**
 * CustomDropdown component - 完全自定义的下拉组件，支持绿色主题
 */
const CustomDropdown = ({
  label,
  options = [],
  value,
  onChange,
  placeholder = '请选择...',
  error,
  helperText,
  disabled = false,
  required = false,
  className = '',
  size = 'medium',
  name,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const listRef = useRef(null);

  // Size styles
  const sizeStyles = {
    small: 'px-3 py-2 text-sm',
    medium: 'px-4 py-3 text-sm',
    large: 'px-4 py-4 text-base'
  };

  // 找到当前选中的选项
  const selectedOption = options.find(option => option.value === value);

  // 处理点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event) => {
      // 检查点击是否在下拉框或其选项上
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // 检查是否点击在Portal渲染的下拉选项上
        const dropdownList = document.querySelector('[data-dropdown-list]');
        if (!dropdownList || !dropdownList.contains(event.target)) {
          setIsOpen(false);
          setFocusedIndex(-1);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // 处理键盘导航
  const handleKeyDown = (event) => {
    if (!isOpen) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        calculatePosition();
        setIsOpen(true);
        setFocusedIndex(0);
      }
      return;
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        setFocusedIndex(prev => (prev + 1) % options.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setFocusedIndex(prev => prev <= 0 ? options.length - 1 : prev - 1);
        break;
      case 'Enter':
        event.preventDefault();
        if (focusedIndex >= 0 && options[focusedIndex]) {
          handleSelect(options[focusedIndex]);
        }
        break;
      case 'Tab':
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };

  // 计算下拉框位置
  const calculatePosition = () => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  // 处理选择
  const handleSelect = (option) => {
    if (option.disabled) return;
    
    const syntheticEvent = {
      target: {
        name: name,
        value: option.value
      }
    };
    
    onChange(syntheticEvent);
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  // 打开下拉框时计算位置
  const toggleDropdown = () => {
    if (!disabled) {
      if (!isOpen) {
        calculatePosition();
      }
      setIsOpen(!isOpen);
    }
  };

  // 基础样式
  const baseStyles = `
    w-full transition-all duration-200 focus:outline-none cursor-pointer
    border border-gray-300 bg-white text-gray-900 rounded-xl
    focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
    hover:border-emerald-300
    disabled:bg-gray-50 disabled:cursor-not-allowed
  `;

  // 错误样式
  const errorStyles = error ? `
    border-red-300 bg-red-50 text-red-900
    focus:ring-red-500 focus:border-red-500
  ` : '';

  // 组合样式
  const triggerClasses = [
    baseStyles,
    sizeStyles[size],
    errorStyles,
    'pr-10', // Space for dropdown arrow
    className
  ].join(' ').replace(/\s+/g, ' ').trim();

  return (
    <div className="w-full" ref={dropdownRef}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* 下拉触发器 */}
      <div className="relative">
        <div
          className={triggerClasses}
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
          tabIndex={disabled ? -1 : 0}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          {...props}
        >
          <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          
          {/* 下拉箭头 */}
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg 
              className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* 下拉选项列表 - 使用Portal渲染到body */}
        {isOpen && createPortal(
          <div 
            data-dropdown-list
            className="fixed z-[9999] bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-auto"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              minWidth: dropdownPosition.width
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <ul 
              ref={listRef}
              role="listbox" 
              className="py-1"
            >
              {options.map((option, index) => (
                <li
                  key={option.value || index}
                  className={`
                    px-4 py-3 text-sm cursor-pointer transition-colors duration-150
                    ${option.disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-900'}
                    ${value === option.value 
                      ? 'bg-emerald-500 text-white font-medium' 
                      : focusedIndex === index 
                        ? 'bg-emerald-50 text-emerald-700' 
                        : 'hover:bg-emerald-50 hover:text-emerald-700'
                    }
                  `}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(option);
                  }}
                  onMouseEnter={() => setFocusedIndex(index)}
                  role="option"
                  aria-selected={value === option.value}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          </div>,
          document.body
        )}
      </div>

      {/* Helper text or error message */}
      {(error || helperText) && (
        <p className={`mt-2 text-sm ${error ? 'text-red-600' : 'text-gray-500'}`}>
          {error || helperText}
        </p>
      )}
    </div>
  );
};

CustomDropdown.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
    disabled: PropTypes.bool
  })).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  className: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  name: PropTypes.string
};

export default CustomDropdown; 